import React, { FC, useCallback } from 'react';

import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  HStack,
  Text,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';

import Input from '@/components/Forms/Input';
import { useAuth } from '@/context/AuthContext';
import { Button, Link } from '@/elements';

const RegisterForm: FC<{ onSubmit?: () => void }> = ({
  onSubmit: onSubmitProp,
}) => {
  const { register: signUp, isRegistering, registerError } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange' });

  const onSubmit = useCallback(async (values) => {
    const success = await signUp(values);

    if (success && onSubmitProp) {
      onSubmitProp();
    }
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <HStack spacing='md'>
        <FormControl isInvalid={errors.firstName}>
          <Input
            control={control}
            label='First name'
            name='firstName'
            rules={{ required: 'This field is required' }}
            type='text'
          />
          {errors.firstName && (
            <FormErrorMessage>{errors.firstName.message}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl isInvalid={errors.lastName}>
          <Input
            control={control}
            label='Last name'
            name='lastName'
            rules={{ required: 'This field is required' }}
            type='text'
          />
          {errors.lastName && (
            <FormErrorMessage>{errors.lastName.message}</FormErrorMessage>
          )}
        </FormControl>
      </HStack>
      <FormControl isInvalid={errors.dob}>
        <Input
          control={control}
          defaultValue={new Date()}
          label='Date of Birth'
          name='dob'
          rules={{ required: 'This field is required' }}
          type='date'
        />
        {errors.dob && (
          <FormErrorMessage>{errors.dob.message}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl isInvalid={errors.email}>
        <Input
          control={control}
          label='Your email'
          name='email'
          rules={{ required: 'This field is required' }}
          type='email'
        />
        {errors.email && (
          <FormErrorMessage>{errors.email.message}</FormErrorMessage>
        )}
      </FormControl>
      <FormControl isInvalid={errors.password}>
        <Input
          control={control}
          label='Create a password'
          name='password'
          rules={{ minLength: 6 }}
          type='password'
        />
        {errors.password && (
          <FormErrorMessage>{errors.password.message}</FormErrorMessage>
        )}
      </FormControl>

      <Text color='whiteAlpha.500' fontSize='12px'>
        The password should be at least 6 characters and contain letters and
        digits
      </Text>

      {registerError && (
        <Text color='red' mt='2'>
          {registerError}
        </Text>
      )}

      <Box mt='54px'>
        <Controller
          control={control}
          name='terms'
          render={({ field: { onChange, onBlur, value } }) => (
            <Checkbox checked={value} onBlur={onBlur} onChange={onChange}>
              <Text color='whiteAlpha.700' fontSize='17px'>
                I agree with{' '}
                <Link
                  color='white'
                  href='/legal/terms-and-conditions'
                  target='_blank'
                >
                  Terms of use
                </Link>{' '}
                and{' '}
                <Link
                  color='white'
                  href='/legal/privacy'
                  target='_blank'
                >
                  Privacy Policy
                </Link>
              </Text>
            </Checkbox>
          )}
          rules={{ required: true }}
        />

        <Button
          data-cy='signUpSubmit'
          isDisabled={!isValid}
          isLoading={isRegistering}
          minWidth={200}
          mt='xl'
          type='submit'
          w='100%'
        >
          Sign Up
        </Button>
      </Box>
    </form>
  );
};

export default RegisterForm;
